import React, {
    useRef,
    useEffect,
    useImperativeHandle,
  } from 'react';
  import Hls, { Events } from 'hls.js';
  
  type SimulcastHlsPlayerProps = {
    src: string;
    config?: Partial<ConstructorParameters<typeof Hls>[0]>;
  
    // HTMLVideoElement props
    autoPlay?: boolean;
    controls?: boolean;
    style?: React.CSSProperties;
    width?: string | number;
    height?: string | number;
  
    // Eventos
    onPlay?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
    onPause?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
    onSeeked?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
    onTimeUpdate?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
  
    // Ref externo
    playerRef?: React.Ref<HTMLVideoElement>;
  };
  
  const SimulcastHlsPlayer: React.FC<SimulcastHlsPlayerProps> = ({
    src,
    config,
    autoPlay = false,
    controls = true,
    style,
    width = '100%',
    height = 'auto',
    onPlay,
    onPause,
    onSeeked,
    onTimeUpdate,
    playerRef,
  }) => {
    const internalRef = useRef<HTMLVideoElement>(null);
  
    // Expõe o ref interno para fora se playerRef for passado
    useImperativeHandle(playerRef, () => internalRef.current as HTMLVideoElement);
  
    useEffect(() => {
      if (!internalRef.current || !src) return;
  
      let hls: Hls | null = null;
  
      if (Hls.isSupported()) {
        hls = new Hls(config);
        hls.loadSource(src);
        hls.attachMedia(internalRef.current);
  
        if (autoPlay) {
          hls.on(Events.MANIFEST_PARSED, () => {
            internalRef.current?.play().catch(() => {
              /* autoplay pode falhar sem interação do usuário */
            });
          });
        }
      } else if (
        internalRef.current.canPlayType('application/vnd.apple.mpegurl')
      ) {
        internalRef.current.src = src;
        internalRef.current.addEventListener('loadedmetadata', () => {
          if (autoPlay) {
            internalRef.current?.play();
          }
        });
      }
  
      return () => {
        hls?.destroy();
      };
    }, [src, config, autoPlay]);
  
    return (
      <video
        ref={internalRef}
        style={{ width, height, ...style }}
        controls={controls}
        autoPlay={autoPlay}
        onPlay={onPlay}
        onPause={onPause}
        onSeeked={onSeeked}
        onTimeUpdate={onTimeUpdate}
      />
    );
  };
  
  export default SimulcastHlsPlayer;
  